// src/holladai/pages/PortfolioDetail.js
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useInventoryStats } from "../hooks/useInventoryStats";
import { useOooData } from "../hooks/useOooData";
import { useMemo, useEffect, useState } from "react";
import "./Dashboard.css";

// For the 7-day chart
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { fetchInventoryStats } from "../services/inventoryStatsService";

const PROPERTIES = {
  58404: "Echo Indy-Ameriplex",
};

// Map raw keys to human-friendly labels
const LABEL_MAP = {
  occupancy: "Occupancy %",
  arrivals: "Arrivals",
  departures: "Departures",
  stayOvers: "Stay Overs",
  totalReserved: "Total Reserved",
};

export default function PortfolioDetail() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const statType = searchParams.get("stat") || "totalReserved";

  const numericHotelId = Number(hotelId);
  const hotelIds = useMemo(() => [numericHotelId], [numericHotelId]);

  const isOoo = statType === "oooRooms";

  // --- Today's totals (top cards) ---
  const { data, loading, error } = isOoo
    ? useOooData(hotelIds)
    : useInventoryStats(hotelIds);

  const hotelData = isOoo ? data?.[numericHotelId] || [] : data?.[numericHotelId] || {};

  // --- Last 7 days occupancy (bottom chart & table) ---
  const [last7DaysData, setLast7DaysData] = useState([]);
  const [loading7Days, setLoading7Days] = useState(false);
  const [error7Days, setError7Days] = useState(null);

  useEffect(() => {
    if (statType !== "occupancy") return;

    const fetchLast7Days = async () => {
      try {
        setLoading7Days(true);
        setError7Days(null);

        const today = new Date();
        const endDate = today.toISOString().split("T")[0];
        const start = new Date();
        start.setDate(today.getDate() - 6);
        const startDate = start.toISOString().split("T")[0];

        const raw = await fetchInventoryStats(hotelIds, startDate, endDate);
        const hotelRaw = raw[hotelIds[0]];

        setLast7DaysData(hotelRaw.dailyOccupancy || []);
      } catch (err) {
        console.error("Error fetching last 7 days occupancy:", err);
        setError7Days(err.message || "Failed to fetch last 7 days occupancy");
      } finally {
        setLoading7Days(false);
      }
    };

    fetchLast7Days();
  }, [statType, hotelIds]);

  if (loading) return <p>Loading property details...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  return (
    <div className="dashboard-container">
      <button className="back-button" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <h1>{PROPERTIES[numericHotelId] || `Property ${numericHotelId}`}</h1>

      {/* --- TOP CARDS: Today's totals --- */}
      <div className="stats-grid">
      {isOoo ? (
        hotelData.length > 0 ? (
          <table className="ooo-table">
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Room ID</th>
              <th style={{ textAlign: "left" }}>Room Type</th>
              <th style={{ textAlign: "left" }}>Status</th>
              <th style={{ textAlign: "left" }}>Reason(s)</th>
            </tr>
          </thead>
          <tbody>
            {hotelData.map((room) => {
              const roomId = room.roomId || "Unknown";
              const roomType = room.roomType?.roomType || "Unknown";
              const status = room.housekeeping?.housekeepingRoomStatus?.housekeepingRoomStatus || "Out-of-Order";
              const reasons = room.outOfOrder?.map((o) => o.reasonDescription).filter(Boolean).join(", ") || "-";

              return (
                <tr key={room.roomId || Math.random()}>
                  <td>{roomId}</td>
                  <td>{roomType}</td>
                  <td>{status}</td>
                  <td>{reasons}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        ) : (
          <div className="stat-card">
            <p className="stat-label">Total Out-of-Order Rooms</p>
            <p className="stat-value">0</p>
          </div>
        )
      ) : (
        Object.entries(hotelData)
          .filter(([key]) => key !== "dailyOccupancy")
          .map(([key, value]) => (
            <div key={key} className="stat-card">
              <p className="stat-label">{LABEL_MAP[key] || key}</p>
              <p className="stat-value">{value ?? 0}</p>
            </div>
          ))
      )}
    </div>




      {/* --- BOTTOM SECTION: Last 7 days occupancy chart + table --- */}
      {statType === "occupancy" && (
        <div className="last7days-container">
          <h2>Last 7 days occupancy</h2>
          {loading7Days ? (
            <p>Loading occupancy chart...</p>
          ) : error7Days ? (
            <p style={{ color: "red" }}>{error7Days}</p>
          ) : last7DaysData.length === 0 ? (
            <p>No occupancy data available</p>
          ) : (
            <>
              {/* Chart */}
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={last7DaysData}>
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="occupancy" stroke="#00aaff" />
                </LineChart>
              </ResponsiveContainer>

              {/* Table */}
              <table className="last7days-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Occupancy %</th>
                    <th>Arrivals</th>
                    <th>Departures</th>
                    <th>Stayovers</th>
                  </tr>
                </thead>
                <tbody>
                  {last7DaysData.map((d) => (
                    <tr key={d.date}>
                      <td>{d.date}</td>
                      <td style={{ textAlign: "center" }}>{d.occupancy}%</td>
                      <td style={{ textAlign: "center" }}>{d.arrivals}</td>
                      <td style={{ textAlign: "center" }}>{d.departures}</td>
                      <td style={{ textAlign: "center" }}>{d.stayOvers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
